import { isScene, type StoryCtx } from '@dispositio/core';
import { create } from 'zustand';
import type { FocusKey } from '../lib/designModel';

export type Selection = { kind: 'node' | 'entity' | 'connection' | 'group'; id: string } | null;

/**
 * Die drei Modi des Arbeitsbereichs: Bibliothek (intern `design`: Personen, Orte, Gegenstände, Organisationen, Konzepte, Welt),
 * Handlung (intern `planning`: Zeitlinie und Erzähllinie) und Manuskript (intern `editor`).
 */
export type View = 'design' | 'planning' | 'editor';

/** Fenster zum Anlegen eines Eintrags: Klasse, ggf. vorbelegter Name, Lage auf der Pinnwand, Kategorie. Bearbeitet wird in der Seitenleiste. */
export type EntityDialog = { typeId: string; name?: string; pos?: { x: number; y: number }; groupId?: string };

/** Das im Texteditor geöffnete Dokument eines Ereignisses oder Zeitraums. */
export interface OpenDocument {
  nodeId: string;
  docId: string;
  /** gleich die möglichen Verweise prüfen („Jetzt korrigieren“ in den Hinweisen) */
  review?: boolean;
}

/** Zuletzt genutzter Modus samt offenem Dokument je Geschichte (im Browser): Beim nächsten Öffnen geht es dort weiter. */
type LastPlace = { view: View; doc: { nodeId: string; docId: string } | null };
const placeKey = (projectId: string): string => `dispositio.place.${projectId}`;
function lastPlace(projectId: string): LastPlace {
  try {
    const p = JSON.parse(window.localStorage.getItem(placeKey(projectId)) ?? 'null') as Partial<LastPlace> | null;
    const view = p?.view === 'design' || p?.view === 'editor' ? p.view : 'planning';
    const doc = view === 'editor' && typeof p?.doc?.nodeId === 'string' && typeof p.doc.docId === 'string' ? { nodeId: p.doc.nodeId, docId: p.doc.docId } : null;
    return { view, doc };
  } catch {
    return { view: 'planning', doc: null }; // ohne Speicher (privates Fenster, Tests)
  }
}
/** Eine neue Geschichte beginnt im Manuskript (die Tutorials in der Handlung, wo ihre Führung beginnt). */
export const startInManuscript = (projectId: string): void => rememberPlace({ projectId, view: 'editor', openDoc: null });

function rememberPlace(s: { projectId: string | null; view: View; openDoc: OpenDocument | null }): void {
  if (!s.projectId) return;
  try {
    const place: LastPlace = { view: s.view, doc: s.view === 'editor' && s.openDoc ? { nodeId: s.openDoc.nodeId, docId: s.openDoc.docId } : null };
    window.localStorage.setItem(placeKey(s.projectId), JSON.stringify(place));
  } catch {
    // ohne Speicher: beim nächsten Mal wieder in der Handlung
  }
}

/** Auswahl, die ein Modus behält: die Bibliothek Einträge und Kategorien, Handlung und Manuskript alles außer Kategorien. */
const keepsSelection = (view: View, sel: Selection): boolean => !!sel && (view === 'design' ? sel.kind === 'entity' || sel.kind === 'group' : sel.kind !== 'group');

interface Workspace {
  projectId: string | null;
  view: View;
  /** Gewähltes Objekt; die Seitenleiste zeigt es (ohne Auswahl in Handlung und Manuskript die Geschichte). */
  selection: Selection;
  /** Vorige Auswahlen, wenn man über Verweise weitergegangen ist (Szene → Person → Ort): „Zurück“ in der Seitenleiste. */
  trail: Selection[];
  /** Seitenleiste ausgeklappt; eine neue Auswahl klappt sie wieder aus. */
  sideOpen: boolean;
  /**
   * Weitere ausgewählte Szenen (Umschalt+Klick auf der Zeitlinie oder in der Erzähllinie); die zuletzt gewählte steht in `selection`
   * und zeigt die Seitenleiste. Jede andere Auswahl leert die Liste.
   */
  alsoSelected: string[];
  openDoc: OpenDocument | null;
  fitTick: number;
  /** Der letzte fitTick kam von „Alles zeigen“ (alles einpassen) */
  fitAll: boolean;
  /**
   * Fokus der Handlungsfläche auf eine Szene (PF 11): `frame` fährt weich auf sie und ihre Nachbarn (Erzähllinie, Tastatur, aus dem
   * Manuskript), `near` nur, falls sie mit ihren Nachbarn nicht zu sehen ist (Klick auf der Fläche). `tick` unterscheidet Aufträge.
   */
  focus: { id: string; mode: 'frame' | 'near'; tick: number } | null;
  /** Bereich der Bibliothek (linke Leiste) */
  designFocus: FocusKey;
  /** Geöffnetes Fenster eines Eintrags (aus jedem Modus) */
  entityDialog: EntityDialog | null;
  /** Tab der Seitenleiste einer Szene bzw. einer Entität (bleibt beim Wechsel zur nächsten derselben Art) */
  sceneTab: SceneTab;
  entityTab: EntityTab;

  open: (projectId: string) => void;
  setView: (view: View) => void;
  /** Auswahl auf der Fläche, in einer Liste o. Ä.: beginnt einen neuen Verlauf */
  select: (sel: Selection) => void;
  selectNode: (id: string | null) => void;
  /** Einem Verweis folgen (ggf. in einen anderen Modus): die bisherige Auswahl kommt in den Verlauf */
  inspect: (sel: NonNullable<Selection>, view?: View) => void;
  /** Zur vorigen Auswahl im Verlauf */
  back: () => void;
  toggleSide: () => void;
  /** Umschalt+Klick: Szene zur Auswahl hinzunehmen bzw. wieder herausnehmen (ist bisher keine Szene gewählt: nur sie wählen) */
  toggleScene: (ctx: StoryCtx, id: string) => void;
  /** Dokument im Texteditor öffnen (wechselt in die Ansicht „Texteditor“) */
  openDocument: (doc: OpenDocument | null) => void;
  fit: () => void;
  /** Die Fläche auf eine Szene fahren (null: keinen Auftrag) */
  focusScene: (id: string | null, mode?: 'frame' | 'near') => void;
  setDesignFocus: (focus: FocusKey) => void;
  /** Fenster zum Anlegen eines Eintrags öffnen; null schließt es */
  openEntityDialog: (dialog: EntityDialog | null) => void;
  setSceneTab: (tab: SceneTab) => void;
  setEntityTab: (tab: EntityTab) => void;
}

/** Tabs einer Szene: Angaben, Zeit, Manuskript, Entitäten darin */
export type SceneTab = 'info' | 'time' | 'manuscript' | 'objects';
/** Tabs einer Entität: Felder, Verlauf (Beziehungen, Zustand, Wissen), Verweise, Weiteres */
export type EntityTab = 'info' | 'history' | 'refs' | 'more';

export const useWorkspace = create<Workspace>((set, get) => ({
  projectId: null,
  view: 'planning',
  selection: null,
  trail: [],
  sideOpen: true,
  alsoSelected: [],
  openDoc: null,
  fitTick: 0,
  fitAll: false,
  focus: null,
  designFocus: 'person',
  entityDialog: null,
  sceneTab: 'info',
  entityTab: 'info',

  // eine andere Geschichte: dort weiter, wo man sie verlassen hat (Modus, im Manuskript das offene Dokument)
  open: (projectId) =>
    set((s) => {
      if (s.projectId === projectId) return s;
      const last = lastPlace(projectId);
      const openDoc = last.doc;
      const selection: Selection = openDoc ? { kind: 'node', id: openDoc.nodeId } : null;
      return { projectId, view: last.view, selection, trail: [], sideOpen: last.view !== 'design', alsoSelected: [], openDoc, entityDialog: null, sceneTab: 'info', entityTab: 'info' };
    }),
  // Beim Wechsel des Modus bleibt die Auswahl nur, wo sie sinnvoll ist (siehe keepsSelection); die Seitenleiste ist offen, in der
  // Bibliothek nur mit Auswahl (dort gibt es ohne Auswahl nichts zu zeigen)
  setView: (view) => {
    set((s) => {
      const selection = keepsSelection(view, s.selection) ? s.selection : null;
      return { view, alsoSelected: [], selection, trail: [], sideOpen: view !== 'design' || selection !== null };
    });
    rememberPlace(get());
  },
  // ohne Auswahl bleibt die Seitenleiste, wie sie ist – nur in der Bibliothek klappt sie ein
  select: (selection) => set((s) => ({ selection, trail: [], alsoSelected: [], sideOpen: selection ? true : s.view === 'design' ? false : s.sideOpen })),
  selectNode: (id) => get().select(id ? { kind: 'node', id } : null),
  inspect: (sel, view) => {
    const before = get();
    const same = before.selection?.kind === sel.kind && before.selection.id === sel.id;
    const trail = before.selection && !same ? [...before.trail, before.selection].slice(-30) : before.trail;
    set({ view: view ?? before.view, selection: sel, trail, alsoSelected: [], sideOpen: true });
    if (view && view !== before.view) rememberPlace(get());
  },
  back: () =>
    set((s) => {
      const prev = s.trail.at(-1);
      return prev ? { selection: prev, trail: s.trail.slice(0, -1), alsoSelected: [], sideOpen: true } : s;
    }),
  toggleSide: () => set((s) => ({ sideOpen: !s.sideOpen })),
  toggleScene: (ctx, id) =>
    set((s) => {
      const primary = s.selection?.kind === 'node' && isScene(ctx.nodes.get(s.selection.id)) ? s.selection.id : null;
      if (!primary || !isScene(ctx.nodes.get(id))) return { selection: { kind: 'node', id }, trail: [], alsoSelected: [], sideOpen: true };
      if (id === primary) {
        // die zuletzt gewählte herausnehmen: die davor gewählte zeigt wieder die Seitenleiste
        const rest = s.alsoSelected.slice(0, -1);
        const before = s.alsoSelected.at(-1);
        return { selection: before ? { kind: 'node', id: before } : null, trail: [], alsoSelected: rest };
      }
      if (s.alsoSelected.includes(id)) return { alsoSelected: s.alsoSelected.filter((x) => x !== id) };
      return { selection: { kind: 'node', id }, trail: [], alsoSelected: [...s.alsoSelected, primary], sideOpen: true };
    }),
  openDocument: (openDoc) => {
    set(openDoc ? { openDoc, view: 'editor', selection: { kind: 'node', id: openDoc.nodeId }, trail: [], alsoSelected: [], sideOpen: true } : { openDoc: null });
    rememberPlace(get());
  },
  fit: () => set({ fitTick: Date.now(), fitAll: true }),
  focusScene: (id, mode = 'frame') => set((s) => ({ focus: id ? { id, mode, tick: (s.focus?.tick ?? 0) + 1 } : null })),
  setDesignFocus: (designFocus) =>
    set((s) => (s.selection?.kind === 'entity' || s.selection?.kind === 'group' ? { designFocus, selection: null, trail: [], sideOpen: s.view === 'design' ? false : s.sideOpen } : { designFocus })),
  openEntityDialog: (entityDialog) => set({ entityDialog }),
  setSceneTab: (sceneTab) => set({ sceneTab }),
  setEntityTab: (entityTab) => set({ entityTab }),
}));
